import { useEffect, useRef, useState } from 'react';
import Dashboard from './Dashboard';
import { BookOpen, Calculator, Database, ChartNoAxesCombined, House, Library } from 'lucide-react';
import VariableVisualization from './VariableVisualization';
import { apiService } from '../services/api';
import { BaseModel } from '../types/model';
import MathText from './shared/Math';
import { datasetLabel } from '../utils/modelTargets';
import 'katex/dist/katex.min.css';
import './ResearchWorkspace.css';

type Page = 'overview' | 'model' | 'computation' | 'calibration' | 'simulation' | 'references';
interface Parameter {
  routeKey?: string; key: string; tex: string; label: string; section: string | null; scalar: boolean;
  framework: string; dataset: string; source: 'params'; unavailable?: string;
}
interface Note {
  pages: Record<Exclude<Page, 'simulation' | 'overview'>, string>;
  anchors: Record<string, string>; parameters: Parameter[];
}
const pages: Page[] = ['model', 'computation', 'calibration', 'simulation', 'references'];
const pageInfo = {
  overview: { title: 'Overview', icon: House },
  model: { title: 'The model', icon: BookOpen },
  computation: { title: 'Computation', icon: Calculator },
  calibration: { title: 'Calibration', icon: Database },
  references: { title: 'References', icon: Library },
  simulation: { title: 'Simulation', icon: ChartNoAxesCombined },
};
export function readResearchRoute(hash: string): { page: Page; anchor: string } {
  const [page, anchor = ''] = hash.replace(/^#\/?/, '').split('/');
  return { page: (page === 'overview' || pages.includes(page as Page)) ? page as Page : 'overview', anchor };
}

function ParameterValues({ parameter }: { parameter: Parameter }) {
  const [model, setModel] = useState<BaseModel | null>(null);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (parameter.unavailable) return;
    let active = true;
    void apiService.loadCalibration(parameter.framework, parameter.dataset).then(values => {
      if (active) setModel(values);
    }).catch(() => {
      if (active) setError('The calibration values could not be loaded. The calibration description remains available.');
    });
    return () => { active = false; };
  }, [parameter, retry]);
  if (parameter.unavailable) return <p role="status">{parameter.unavailable}</p>;
  if (error) return <p role="alert">{error} <button onClick={() => { setError(''); setRetry(n => n + 1); }}>Retry values</button></p>;
  if (!model) return <p role="status">Loading calibration values…</p>;
  if (model.params[parameter.key] === undefined || model.params[parameter.key] === null) return <p>This parameter is not supplied by the current dataset.</p>;
  return <>
    <p className="note-caption">Stored calibration inputs · {parameter.framework} · {datasetLabel(parameter.dataset)}</p>
    <VariableVisualization key={parameter.key} model={model} mode="single" initialVariable={parameter.key} lockParameter />
  </>;
}

export default function ResearchWorkspace() {
  const [route, setRoute] = useState(() => readResearchRoute(window.location.hash));
  const [note, setNote] = useState<Note | null>(null);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [indexOpen, setIndexOpen] = useState(false);
  const [simulationVisited, setSimulationVisited] = useState(route.page === 'simulation');
  const remembered = useRef<Partial<Record<Page, string>>>({});
  useEffect(() => {
    const update = () => setRoute(readResearchRoute(window.location.hash));
    const repeatLink = (event: MouseEvent) => {
      const link = (event.target as Element).closest('a');
      if (link?.hash && link.hash === window.location.hash) update();
    };
    document.addEventListener('click', repeatLink);
    window.addEventListener('hashchange', update);
    return () => { window.removeEventListener('hashchange', update); document.removeEventListener('click', repeatLink); };
  }, []);
  useEffect(() => {
    let active = true;
    fetch('/research-note.json', { cache: 'no-cache' }).then(r => {
      if (!r.ok) throw new Error();
      return r.json();
    }).then(data => { if (active) setNote(data); }).catch(() => {
      if (active) setError('The research note could not be loaded.');
    });
    return () => { active = false; };
  }, [retry]);
  const parameter = note?.parameters.find(p => route.page === 'calibration' && route.anchor === `parameter-${p.routeKey || p.key}`);
  const focused = parameter && !parameter.scalar ? parameter : undefined;
  useEffect(() => {
    remembered.current[route.page] = window.location.hash;
    setIndexOpen(false);
    if (route.page === 'simulation') setSimulationVisited(true);
    const target = focused ? document.getElementById('calibration-focus') : document.getElementById(route.anchor);
    if (target) {
      for (let el: HTMLElement | null = target; el; el = el.parentElement) {
        if (el instanceof HTMLDetailsElement) el.open = true;
      }
      target.classList.add('note-target');
      requestAnimationFrame(() => target.scrollIntoView({ block: 'start' }));
      const highlightTimer = window.setTimeout(() => target.classList.remove('note-target'), 1600);
      return () => { window.clearTimeout(highlightTimer); target.classList.remove('note-target'); };
    }
    document.querySelector('main')?.scrollTo?.({ top: 0 });
  }, [route, note, focused]);
  let procedure = '';
  if (focused && note) {
    const doc = new DOMParser().parseFromString(note.pages.calibration, 'text/html');
    const section = doc.getElementById(focused.section || '');
    if (section?.tagName === 'DETAILS') procedure = section.querySelector(':scope > .subsection-content')?.innerHTML || '';
    else if (section) {
      for (let el = section.nextElementSibling; el && !/^H[12]$/.test(el.tagName); el = el.nextElementSibling) procedure += el.outerHTML;
    }
    procedure = procedure.replace(/ id="[^"]*"/g, '');
  }
  const isNotePage = route.page !== 'simulation' && route.page !== 'overview';
  const content = isNotePage && note ? note.pages[route.page as keyof Note['pages']] : '';
  const contents = content ? Array.from(new DOMParser().parseFromString(content, 'text/html').querySelectorAll('h2, details.subsection > summary > h3')).map(h => {
    const label = h.cloneNode(true) as HTMLElement;
    label.querySelectorAll('.katex-mathml').forEach(el => el.remove());
    return { title: label.textContent || '', id: h.id || h.closest('details')?.id || '', level: h.tagName };
  }) : [];
  const groups: { title: string; id: string; children: typeof contents }[] = [];
  contents.forEach(heading => {
    if (heading.level === 'H2') groups.push({ ...heading, children: [] });
    else groups[groups.length - 1]?.children.push(heading);
  });
  // Keep the source section anchor, but avoid repeating the page title.
  const displayedContent = route.page === 'calibration' || route.page === 'computation' || route.page === 'references'
    ? content.replace(/<h2 id="([^"]+)"[^>]*>[\s\S]*?<\/h2>/, '<span id="$1" class="section-anchor"></span>')
    : content;
  return <div className="research-workspace">
    <nav id="research-navigation" className="research-nav" aria-label="Research sections">
      {(['overview', ...pages] as Page[]).map(page => {
        const Icon = pageInfo[page].icon;
        return <a key={page} href={remembered.current[page] || `#/${page}`} aria-current={route.page === page ? 'page' : undefined}>
          <Icon size={18} strokeWidth={1.6} aria-hidden="true" /><span>{pageInfo[page].title}</span>
        </a>;
      })}
    </nav>
    <div className="workspace-page">
    {route.page !== 'overview' && <header className="page-heading">
      <h1>{pageInfo[route.page].title}</h1>
    </header>}
    {route.page === 'overview' && <section className="research-overview">
      <h1>Trade model analysis</h1>
      <p className="overview-intro">CP2015 and Furusawa2023: model documentation, calibration and policy simulations.</p>
      <div className="overview-cards">
        <a href="#/model" className="overview-card"><h2>The model</h2><p>Model setup, dynamic equilibrium, stationary equilibrium, and hat algebra.</p></a>
        <a href="#/computation" className="overview-card"><h2>Computation</h2><p>Inputs, baseline construction, solution steps, and counterfactual comparisons.</p></a>
        <a href="#/calibration" className="overview-card"><h2>Calibration</h2><p>Parameter values, data sources, estimation procedures, and interactive baseline inputs.</p></a>
        <a href="#/simulation" className="overview-card"><h2>Simulation</h2><p>Inspect baselines, specify policy changes, and compare results for Furusawa2023 and CP2015.</p></a>
        <a href="#/references" className="overview-card"><h2>References</h2><p>Cited literature.</p></a>
      </div>

    </section>}
    {isNotePage && <div className={`research-note research-note-${route.page}`}>
      {error && <p role="alert">{error} <button onClick={() => { setError(''); setRetry(n => n + 1); }}>Retry note</button></p>}
      {!note && !error && <p role="status">Loading research note…</p>}
      {!focused && route.page !== 'references' && contents.length > 0 && <>
      <button className="contents-toggle" aria-expanded={indexOpen} aria-controls="note-index" onClick={() => setIndexOpen(open => !open)}>
        On this page <span aria-hidden="true">{indexOpen ? '−' : '+'}</span>
      </button>
      <nav id="note-index" className={`note-contents${indexOpen ? ' is-open' : ''}`} aria-label="On this page">
        {groups.map(group => <section className="contents-group" key={group.id}>
          <h3><a href={`#/${route.page}/${group.id}`}>{group.title}</a></h3>
          {group.children.length > 0 && <ul>{group.children.map(h => <li key={h.id}><a href={`#/${route.page}/${h.id}`}>{h.title}</a></li>)}</ul>}
        </section>)}
      </nav></>}
      <article className="note-body">
      {focused && <section id="calibration-focus" className="calibration-focus">
        <a href="#/calibration">All calibration inputs</a>
        <h2>{focused.label} <MathText text={`$${focused.tex}$`} /></h2>
        <div dangerouslySetInnerHTML={{ __html: procedure }} />
        <h3>Stored calibration values</h3>
        <ParameterValues key={`${focused.framework}/${focused.dataset}/${focused.key}`} parameter={focused} />
      </section>}
      {focused ? <details className="full-calibration"><summary>Full calibration reference</summary><div dangerouslySetInnerHTML={{ __html: content }} /></details>
        : <div className="note-prose" dangerouslySetInnerHTML={{ __html: displayedContent }} />}
      </article>
    </div>}
    {simulationVisited && <div hidden={route.page !== 'simulation'}><Dashboard /></div>}
    </div>
  </div>;
}
